import {
  createCodeableConcept,
  createIdentifier,
  createQuantityWithValue,
  createReference,
  createReferenceFromResource,
  getCodes,
  getDisplayFromCodableConcepts,
  getIdentifier,
  getQuantityWithValue,
  IdentifierCode,
  medpraxSystem,
  QuantityCode,
  system,
  type WithId,
} from '@quromedical/fhir-common';
import { first } from '@quromedical/utils';
import type { Practitioner } from 'fhir/r4';
import { getValidReference } from '../mappings/reference';

import type {
  Consumable,
  ConsumableCreateRequest,
  ConsumableUsage,
} from './schema';

export const mapCreateModelToChargeItem = (
  consumable: ConsumableCreateRequest,
  performer: WithId<Practitioner>,
): fhir4.ChargeItem[] => {
  const { patient, admission, consumables } = consumable;

  const result = consumables.map((consumable) => {
    const { medpraxCode, dateOfUse, quantity, name, nappiCode } = consumable;

    const identifier = createIdentifier(
      IdentifierCode.chargeItemConsumableCode,
      medpraxCode,
      medpraxSystem,
    );

    const codingValue = nappiCode ? nappiCode : medpraxCode;
    const code = createCodeableConcept(codingValue, name, name, system);

    const chargeItem: fhir4.ChargeItem = {
      code,
      resourceType: 'ChargeItem',
      identifier: [identifier],
      quantity: createQuantityWithValue({
        value: quantity,
        code: QuantityCode.ChargeItemConsumable,
      }),
      subject: createReference('Patient', patient),
      context: createReference('Encounter', admission),
      performer: [
        {
          actor: createReferenceFromResource(performer),
        },
      ],
      occurrenceDateTime: dateOfUse,
      status: 'billable',
    };

    return chargeItem;
  });

  return result;
};

export const mapModelToChargeItem = (
  consumable: Consumable,
): fhir4.ChargeItem => {
  const {
    name,
    medpraxCode,
    quantity,
    nappiCode,
    patient,
    admission,
    dateOfUse,
    performer,
    status,
  } = consumable;

  const identifier = createIdentifier(
    IdentifierCode.chargeItemConsumableCode,
    medpraxCode,
    medpraxSystem,
  );

  const codableConcept = createCodeableConcept(nappiCode, name, name);

  return {
    code: codableConcept,
    resourceType: 'ChargeItem',
    identifier: [identifier],
    quantity,
    subject: patient,
    context: admission,
    performer: [
      {
        actor: performer,
      },
    ],
    occurrenceDateTime: dateOfUse,
    status,
  };
};

export const mapChargeItemToModel = (
  fhir: WithId<fhir4.ChargeItem>,
): Partial<Consumable> => {
  const {
    code,
    identifier,
    subject,
    context,
    quantity,
    performer,
    occurrenceDateTime,
    status,
  } = fhir;

  const nappiCode = first(getCodes(code));
  const consumableCode = getIdentifier(
    IdentifierCode.chargeItemConsumableCode,
    identifier,
  );
  const name = getDisplayFromCodableConcepts([code]);

  const patient = getValidReference(subject);
  const admission = getValidReference(context);

  const actor = first(performer)?.actor;
  const practitioner = getValidReference(actor);

  return {
    medpraxCode: consumableCode,
    nappiCode,
    name,
    quantity,
    patient,
    admission,
    performer: practitioner,
    dateOfUse: occurrenceDateTime,
    status,
  };
};

export const mapChargeItemToUsageModel = (
  fhir: WithId<fhir4.ChargeItem>[],
): Partial<ConsumableUsage>[] => {
  return fhir.map<Partial<ConsumableUsage>>((chargeItem) => {
    const model = mapChargeItemToModel(chargeItem);

    const { name, quantity, status } = model;

    const numberQuantity = getQuantityWithValue(quantity)?.value;

    return {
      id: chargeItem.id,
      name: name,
      quantity: numberQuantity,
      performer: model.performer,
      dateOfUse: model.dateOfUse,
      status,
    };
  });
};
